import React, { useEffect, useState } from 'react';
import { initFirebase, authApi, authApiIdempotent, onAuth, trackEvent } from '../firebase/config.js';
import { trackPixels } from '../analytics/pixels.js';
import { ENV } from '../config/env.js';

const API_BASE_URL = ENV.apiBaseUrl;

const CheckoutSuccess = () => {
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);
    const [authReady, setAuthReady] = useState(false);
    const [firedConversion, setFiredConversion] = useState(false);

    useEffect(() => {
        const url = new URL(window.location.href);
        const sessionId = url.searchParams.get('session_id');
        (async () => {
            await initFirebase();
            let unsub = null;
            let fetched = false;
            const confirmAndFetch = async (user, retryCount = 0, maxRetries = 8) => {
                try {
                    if (sessionId && retryCount === 0) {
                        try {
                            console.log('[CheckoutSuccess] confirming session:', { sessionId });
                            try {
                                const meta = await authApiIdempotent(`${API_BASE_URL}/api/stripe/confirm-session`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId }) });
                                window.__ff_sale_meta = meta || null;
                            } catch (_) {
                                window.__ff_sale_meta = null;
                            }
                        } catch (e) { 
                            console.warn('[CheckoutSuccess] confirm-session request failed (webhook may process instead):', e);
                        }
                    }
                    const data = await authApi(`${API_BASE_URL}/api/me/entitlements`);
                    console.log('[CheckoutSuccess] entitlements fetched:', { tier: data?.tier, creditsBalance: data?.creditsBalance, retryCount });
                    
                    setStatus({ ok: true, data });

                    if (data.tier === 'free' && retryCount < maxRetries) {
                        console.log('[CheckoutSuccess] tier still free, retrying after delay...', { retryCount, maxRetries });
                        setTimeout(() => {
                            confirmAndFetch(user, retryCount + 1, maxRetries);
                        }, 500 + (retryCount * 300));
                        return;
                    }
                    // Mark auth as ready only if user is authenticated (non-anonymous)
                    if (user && !user.isAnonymous) {
                        setAuthReady(true);
                    }
                } catch (e) {
                    console.error('[CheckoutSuccess] confirmAndFetch error:', { code: e?.code, requestId: e?.requestId, status: e?.status }, e);
                    setStatus({ ok: false });
                    // Mark auth as ready anyway to allow user to continue
                    if (user && !user.isAnonymous && retryCount > 0) {
                        setAuthReady(true);
                    }
                } finally {
                    if (retryCount === 0) setLoading(false);
                }
            };
            unsub = onAuth(async (u) => {
                if (fetched) return;
                if (u) {
                    fetched = true;
                    await confirmAndFetch(u);
                    if (unsub) unsub();
                }
            });
            // Fallback: try once in case user is already available, wait longer for auth rehydration
            setTimeout(() => { 
                if (!fetched) {
                    const auth = window.firebase_auth_instance;
                    confirmAndFetch(auth?.currentUser); 
                }
            }, 1500);
            // Safety: if auth still not ready after 5s, proceed anyway (user can manually click)
            setTimeout(() => {
                if (!authReady) {
                    console.warn('[CheckoutSuccess] auth not ready after timeout, allowing manual navigation');
                    setAuthReady(true);
                }
            }, 5000);
        })();
    }, [authReady]);

    // Fire purchase/subscribe conversion once when entitlements update
    useEffect(() => {
        try {
            if (!firedConversion && status?.ok) {
                const ent = status.data || {};
                const tier = (ent.tier || 'free');
                const isSub = ['basic','pro','elite'].includes(tier);
                const eventName = isSub ? 'subscribe' : 'purchase';
                const meta = window.__ff_sale_meta || {};
                const params = {
                    transaction_id: (new URL(window.location.href)).searchParams.get('session_id') || undefined,
                    value: meta.value,
                    currency: meta.currency || 'USD',
                };
                try { trackEvent(eventName, params); } catch (_) {}
                try { trackPixels('Purchase', { content_name: isSub ? `sub_${tier}` : 'credits', value: meta.value, currency: meta.currency }); } catch (_) {}
                setFiredConversion(true);
            }
        } catch (_) {}
    }, [status, firedConversion]);

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #111827 0%, #0f172a 100%)',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <header style={{
                width: '100%',
                padding: '16px 0',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <a href="/app/home" style={{
                    textDecoration: 'none',
                    fontWeight: 700,
                    fontSize: 18,
                    backgroundImage: 'linear-gradient(135deg,#14B8A6,#10B981)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                }}>FlashFonic</a>
            </header>
            <main style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px'
            }}>
                <div style={{
                    width: '100%',
                    maxWidth: 560,
                    background: 'rgba(31,41,55,0.9)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 16,
                    padding: 24,
                    color: 'white',
                    textAlign: 'center',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
                }}>
                    <h2 style={{
                        marginTop: 0,
                        marginBottom: 8,
                        backgroundImage: 'linear-gradient(135deg,#3B82F6,#14B8A6,#10B981)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}>Payment successful</h2>
                    <p style={{ color: 'rgba(255,255,255,0.8)' }}>Thank you! Your account will update momentarily.</p>
                    {loading ? (
                        <p>Verifying your session…</p>
                    ) : !authReady ? (
                        <p style={{ color: 'rgba(251,191,36,0.9)' }}>Restoring your account…</p>
                    ) : status?.ok ? (
                        <div style={{ marginTop: 12, display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <div style={{ padding: '8px 12px', borderRadius: 12, background: 'rgba(20,184,166,0.15)', border: '1px solid rgba(20,184,166,0.4)' }}>
                                Tier: <strong>{status.data.tier}</strong>
                            </div>
                            <div style={{ padding: '8px 12px', borderRadius: 12, background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)' }}>
                                Credits: <strong>{status.data.creditsBalance}</strong>
                            </div>
                        </div>
                    ) : (
                        <p>Unable to load updated entitlements right now. You can still continue.</p>
                    )}
                    {/* Auto-redirect to the app only after auth is confirmed */}
                    {authReady && (
                        <script dangerouslySetInnerHTML={{ __html: `
                          (function(){
                            try {
                              // mark stripe return so app can re-check auth and entitlements
                              var u=new URL(window.location.href); u.searchParams.set('stripe','return');
                              window.history.replaceState(null,'',u.toString());
                            } catch(_){ }
                            // Small delay to let UI update, then redirect
                            setTimeout(function(){
                              try { window.location.replace('/app/home'); } catch(_) { window.location.href='/app/home'; }
                            }, 600);
                          })();
                        ` }} />
                    )}
                    <div style={{ marginTop: 16 }}>
                        <a href="/app/home" style={{
                            display: 'inline-block',
                            width: '100%',
                            maxWidth: 320,
                            padding: '12px 16px',
                            background: 'linear-gradient(135deg,#14B8A6,#10B981)',
                            borderRadius: 12,
                            color: 'white',
                            textDecoration: 'none',
                            fontWeight: 600
                        }}>Go to app</a>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default CheckoutSuccess;
